/**
 * @file meetingListTools.tsx
 * @brief The Boardrooms list's filters, tag editor and share sheet, on the web.
 *
 * @details
 * DES (30-09-2026, in the desktop app): "is per filter by tags, add tags,
 * share meeting, filter by date and range kuch nahin hai". Each piece is the
 * phone's, from `BoardroomSessionsScreen.tsx` and `ShareAccessSheet.tsx`:
 * - **Date range:** lives in `components/ui/DateRangePopover.tsx` (the
 *   desktop picker, 30-09-2026); presets in `meetingListLogic.ts`.
 * - **Tag filter:** OR across the chosen tags, from every tag on a meeting the
 *   viewer can see (`available_tags`, never narrowed by the filters).
 * - **Tags:** owner only (the server's rule on PATCH `/tags`).
 * - **Share:** owner only. Lists the owner, a scheduled meeting's invitees
 *   and everyone it is shared with; adds a colleague
 *   from the company list or any Verblizr account by email. The server
 *   refuses an email with no account and says so.
 * - **Company data controls** (ADR-051, DES 21-10-2026): when the user's
 *   company only shares inside, the panel says so in one quiet line and a
 *   Verblizr account outside the company found by email cannot be picked.
 *   The server refuses anyway (403 SHARE_EXTERNAL_BLOCKED).
 * - **Who has access** (sharing v2, DES 21-10-2026): the same panel opens
 *   from the card's "Shared with" line and, for a company admin, on a
 *   member's meeting. The server says what this viewer may do (`canAdd`:
 *   owner only; `canRemove`: owner or company admin, an admin removal is
 *   logged) and flags anyone outside the company as External.
 * - **Remove an invitee** (B45, DES 01-11-2026): an invitee gets the same ✕
 *   as a shared person when the server sends `canRemove: true` on them (never
 *   the owner, never the viewer). The confirm: "Remove <Name>'s access to
 *   this meeting? They won't see it any more." Add people gives it back.
 *
 * No new vendor cost: every call is a plain database read or write.
 *
 * @references
 * - BACKEND boardroom/routes.js (GET list ?from&to&tag, PATCH /tags,
 *   GET /access, POST /shares, DELETE /shares/:who)
 * - FRONTEND ShareAccessSheet.tsx, BoardroomSessionsScreen.tsx
 *
 * @author DES
 * @date 30-09-2026
 */

import { PersonPhoto } from "@/components/ui/PersonPhoto";
import { canRemovePerson, removeConfirmText, type RemovableKind } from "./accessRemoval";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, LoaderCircle, Plus, Tag, UserPlus, X } from "lucide-react";
import { SearchableMenuList } from "@/components/ui/SearchableMenuList";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/DropdownMenu";
import { ApiClientError, apiDelete, apiGet, apiPost } from "@/shared/api/client";
import { boardroomEndpoints, meetingsEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { companyMeetingsKeys, useAssignablePeople } from "@/features/company-meetings/api";
import { useDataControls } from "@/shared/auth/meExtras";

// ----------------------------------------------------------------------
// Filter menus (DES 30-09-2026): a dropdown under the circle, like the
// row's "⋯" menu. The phone opens a sheet (VZRangePickerModal) for a thumb;
// a mouse gets the same choices, labels, icons and order in one click.
// ----------------------------------------------------------------------
/** The tag menu: the shared searchable list (DES 30-09-2026), multi-select —
 *  a meeting with any of the ticked tags shows. */
export function TagFilterItems({
  available,
  selected,
  onChange,
}: {
  available: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  // Keep a chosen tag listed even once no meeting in view carries it.
  const all = [...available, ...selected.filter((t) => !available.includes(t))];
  return (
    <SearchableMenuList
      title="Filter by tag"
      multiple
      searchPlaceholder="Search tags"
      emptyText="No meeting has a tag yet. Add one from a meeting's ⋯ menu, under Tags."
      items={all.map((tag) => ({
        value: tag,
        label: tag,
        icon: <Tag className="h-4 w-4 text-brand" />,
      }))}
      selected={selected}
      onSelect={(tag) =>
        onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag])
      }
    />
  );
}

// ----------------------------------------------------------------------
// Share (owner only)
// ----------------------------------------------------------------------
type SharePerson = {
  userId: string | null;
  name: string;
  email: string;
  avatarUrl?: string | null;
  /** Outside the owner's company (sharing v2). */
  isExternal?: boolean | null;
  sharedAt?: string | null;
  /** Invitees only (ADR-051): false = outside the company while sharing is
   *  inside-only, so listed but with no access. */
  hasAccess?: boolean | null;
  /** B45: this viewer may remove this person. Absent (older server):
   *  shared people follow the top-level `canRemove`, invitees are fixed. */
  canRemove?: boolean | null;
};
type ShareAccess = {
  owner: SharePerson | null;
  invitees: SharePerson[];
  shared: SharePerson[];
};
/** GET /access, POST /shares and DELETE /shares/:who all answer with this.
 *  Older servers send `access` only: owner rights, as before. */
type AccessResponse = {
  access: ShareAccess;
  viewerRole?: "owner" | "admin" | null;
  canAdd?: boolean | null;
  canRemove?: boolean | null;
};

type EmailLookup = {
  status: "idle" | "checking" | "found" | "notfound" | "inlist";
  name?: string;
  profileImageUrl?: string | null;
  reason?: "self" | "company";
};
type ParticipantLookupResult =
  | { isRegistered: false }
  | { isRegistered: true; inYourList: true; reason: "self" | "company"; name: string }
  | {
      isRegistered: true;
      inYourList?: false;
      name: string;
      email: string;
      profileImageUrl?: string | null;
    };

export const meetingAccessKey = (id: string) =>
  [...companyMeetingsKeys.all, "access", id] as const;
const accessKey = meetingAccessKey;

/** Who has access, for the Share pill, the card line and the admin view.
 *  A 404 (not the owner or this company's admin, or sharing switched off)
 *  is an error the caller can use to hide its button. */
export function useMeetingAccess(meetingId: string, enabled = true) {
  return useQuery({
    queryKey: accessKey(meetingId),
    enabled,
    retry: false,
    queryFn: async () =>
      apiGet<AccessResponse>(`${boardroomEndpoints.byId(meetingId)}/access`),
  });
}

/** "21 Oct" this year, "21 Oct 2025" before. */
function sharedWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, d.getFullYear() === new Date().getFullYear() ? "d MMM" : "d MMM yyyy");
}

function errorText(err: unknown): string {
  return err instanceof ApiClientError ? err.message : "Please try again.";
}

function Avatar({ p, size = "md" }: { p: SharePerson; size?: "sm" | "md" }) {
  const label = (p.name || p.email || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-app-quaternary font-bold text-app-text",
        size === "sm" ? "h-7 w-7 text-[10px]" : "h-10 w-10 text-xs",
      )}
    >
      <PersonPhoto src={p.avatarUrl} fallback={label} />
    </span>
  );
}

/**
 * Share, as a menu (DES 30-09-2026: "iski look and feel aisi hi ho ... and
 * should open like this as well", pointing at the row's "⋯" menu). Render it
 * inside a `DropdownMenuContent` (the detail's Share pill) or a
 * `DropdownMenuSubContent` (the "⋯" menu's Share). One panel, the menu's own
 * look: "Add people" search with its matches in place, then "People with
 * access" (owner, invited, shared with ✕). Picking someone shares at once
 * and keeps the menu open.
 */
export function MeetingShareMenu({
  meetingId,
  initialQuery = "",
  listOnly = false,
}: {
  meetingId: string;
  /** Prefills the search, e.g. the person a to-do-only line names (12-10-2026). */
  initialQuery?: string;
  /** "Who has access" from a card's "⋯" menu: the same panel without its
   *  "Add people" part (22-10-2026). */
  listOnly?: boolean;
}) {
  const qc = useQueryClient();
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const access = useMeetingAccess(meetingId);
  // Older servers send no rights: the owner's, as before sharing v2.
  const canAdd = !listOnly && (access.data ? access.data.canAdd !== false : false);
  const canRemove = access.data ? access.data.canRemove !== false : false;
  const people = useAssignablePeople(canAdd);
  const { canShareOutside, shareLine } = useDataControls();

  const onAccess = (res: AccessResponse) => {
    qc.setQueryData<AccessResponse>(accessKey(meetingId), (prev) => ({
      ...prev,
      ...res,
      viewerRole: res.viewerRole ?? prev?.viewerRole,
      canAdd: res.canAdd ?? prev?.canAdd,
      canRemove: res.canRemove ?? prev?.canRemove,
    }));
    // Every list's card line reads `sharing`: refresh them all (the
    // meetings lists live under the company prefix, Home under its own).
    void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
    void qc.invalidateQueries({ queryKey: ["home", "meetings"] });
  };
  const add = useMutation({
    mutationFn: (target: { userId?: string; email?: string }) =>
      apiPost<AccessResponse>(`${boardroomEndpoints.byId(meetingId)}/shares`, target),
    onSuccess: (res) => {
      onAccess(res);
      setQuery("");
      toast({ variant: "success", title: "Shared" });
    },
    onError: (err) =>
      toast({ variant: "destructive", title: "Could not share", description: errorText(err) }),
  });
  const remove = useMutation({
    mutationFn: (who: string) =>
      apiDelete<AccessResponse>(
        `${boardroomEndpoints.byId(meetingId)}/shares/${encodeURIComponent(who)}`,
      ),
    onSuccess: onAccess,
    onError: (err) =>
      toast({
        variant: "destructive",
        title: "Could not remove access",
        description: errorText(err),
      }),
  });

  // The menu takes focus on open; hand it to the search a tick later
  // (once we know this viewer may add people at all).
  useEffect(() => {
    if (!canAdd) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [canAdd]);

  const data = access.data?.access;
  const taken = useMemo(() => {
    const set = new Set<string>();
    for (const p of [data?.owner, ...(data?.invitees ?? []), ...(data?.shared ?? [])]) {
      if (p?.userId) set.add(p.userId);
      if (p?.email) set.add(p.email.toLowerCase());
    }
    return set;
  }, [data]);
  const q = query.trim().toLowerCase();
  const candidates = (people.data ?? []).filter(
    (p) =>
      !taken.has(p.id) &&
      !taken.has((p.email ?? "").toLowerCase()) &&
      (!q || `${p.name ?? ""} ${p.email ?? ""}`.toLowerCase().includes(q)),
  );
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q);

  // The phone's email lookup (ShareAccessSheet + useParticipantEmailLookup,
  // DES 30-09-2026: "share logic same hi rakhna, including search logic"):
  // a full email is checked 450ms after typing stops, and only a Verblizr
  // account that is not you or a colleague can be shared with by email.
  const [lookup, setLookup] = useState<EmailLookup>({ status: "idle" });
  useEffect(() => {
    if (!looksLikeEmail) {
      setLookup({ status: "idle" });
      return;
    }
    let cancelled = false;
    setLookup({ status: "checking" });
    const t = window.setTimeout(async () => {
      try {
        const res = await apiGet<ParticipantLookupResult>(
          `${meetingsEndpoints.participantLookup}?email=${encodeURIComponent(q)}`,
        );
        if (cancelled) return;
        if (res.isRegistered && res.inYourList) {
          setLookup({ status: "inlist", name: res.name, reason: res.reason });
        } else if (res.isRegistered) {
          setLookup({
            status: "found",
            name: res.name,
            profileImageUrl: res.profileImageUrl ?? null,
          });
        } else {
          setLookup({ status: "notfound" });
        }
      } catch {
        if (!cancelled) setLookup({ status: "idle" });
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, looksLikeEmail]);
  // A Verblizr account found by email is outside the company (colleagues
  // come back as "inlist"): not offered while sharing is inside-only.
  const emailRow = lookup.status === "found" && canShareOutside;
  const busy = add.isPending || remove.isPending;

  // ✕ on a shared person or, B45, on an invitee (accessRemoval.ts).
  const removeButton = (p: SharePerson, kind: RemovableKind) => {
    const who = p.name || p.email;
    const message = removeConfirmText(who, kind, access.data?.viewerRole);
    const hint = kind === "invitee" ? "Remove access" : "Stop sharing";
    return (
      <button
        type="button"
        disabled={busy}
        aria-label={kind === "invitee" ? `Remove ${who}'s access` : `Stop sharing with ${who}`}
        title={hint}
        onClick={() => {
          if (window.confirm(message)) remove.mutate(p.userId || p.email);
        }}
        className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    );
  };
  const showMatches = searching || q.length > 0;
  const label = "text-[10px] uppercase tracking-wider text-muted-foreground";

  const personLine = (p: SharePerson, note?: string) => (
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate">{p.name || p.email}</span>
        {p.isExternal ? (
          <span className="shrink-0 text-[9px] font-semibold uppercase text-amber-700 dark:text-amber-500">
            External
          </span>
        ) : null}
      </span>
      {(p.email && p.name && p.email !== p.name) || p.sharedAt ? (
        <span className="block truncate text-xs text-muted-foreground">
          {[
            p.email && p.name && p.email !== p.name ? p.email : null,
            // When it was shared (the admin's "who shared it and when"; the
            // owner is always the one who shares).
            p.sharedAt ? `Shared ${sharedWhen(p.sharedAt)}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      ) : null}
      {note ? <span className="block text-xs text-muted-foreground">{note}</span> : null}
    </span>
  );

  return (
    <div className="w-80">
      {/* Add people: the owner only (an admin can only remove). Shown
          while the rights load, so the owner's search does not jump in. */}
      {listOnly || (access.data && !canAdd) ? null : (
      <>
      <DropdownMenuLabel className={label}>Add people</DropdownMenuLabel>
      <div className="px-1 pb-1.5">
        <div className="flex h-9 items-center gap-2 rounded-full bg-field px-3">
          {add.isPending ? (
            <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-brand" />
          ) : (
            <UserPlus className="h-4 w-4 shrink-0 text-brand" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSearching(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                e.currentTarget
                  .closest('[role="menu"]')
                  ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
                  ?.focus();
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                if (candidates[0]) add.mutate({ userId: candidates[0].id });
                else if (emailRow) add.mutate({ email: q });
              }
              // Typing stays out of the menu's typeahead.
              if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
            }}
            disabled={busy}
            placeholder="Name or email"
            aria-label="Add people by name or email"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {showMatches ? (
        <div className="max-h-52 overflow-y-auto">
          {people.isLoading ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">Loading…</p>
          ) : candidates.length === 0 && lookup.status === "idle" ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">
              {q
                ? "No colleague matches. Type a full email for anyone on Verblizr."
                : "Everyone in your company is on it."}
            </p>
          ) : (
            <>
              {/* The email block first, as on the phone (the list header). */}
              {lookup.status === "checking" ? (
                <p className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <LoaderCircle className="h-4 w-4 animate-spin text-brand" />
                  Checking Verblizr account…
                </p>
              ) : lookup.status === "found" && !canShareOutside ? (
                <p className="flex items-start gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  {lookup.name || q} is outside your company.
                </p>
              ) : lookup.status === "found" ? (
                <DropdownMenuItem
                  disabled={busy}
                  onSelect={(e) => {
                    e.preventDefault();
                    add.mutate({ email: q });
                  }}
                >
                  <span className="mr-2 flex shrink-0">
                    <Avatar
                      p={{
                        userId: null,
                        name: lookup.name ?? q,
                        email: q,
                        avatarUrl: lookup.profileImageUrl,
                      }}
                      size="sm"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">Share with {lookup.name || q}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      Verblizr user · {q}
                    </span>
                  </span>
                  <UserPlus className="ml-2 h-4 w-4 shrink-0 text-brand" />
                </DropdownMenuItem>
              ) : lookup.status === "notfound" ? (
                <p className="flex items-start gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  No Verblizr account uses this email. Only Verblizr users can be given a meeting.
                </p>
              ) : lookup.status === "inlist" ? (
                <p className="flex items-start gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  {lookup.reason === "self"
                    ? "That is you."
                    : "A colleague: pick them from the list below."}
                </p>
              ) : null}
              {candidates.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  disabled={busy}
                  onSelect={(e) => {
                    e.preventDefault();
                    add.mutate({ userId: p.id });
                  }}
                >
                  <span className="mr-2 flex shrink-0">
                    <Avatar
                      p={{
                        userId: p.id,
                        name: p.name ?? "",
                        email: p.email ?? "",
                        avatarUrl: p.avatarUrl,
                      }}
                      size="sm"
                    />
                  </span>
                  {personLine({ userId: p.id, name: p.name ?? "", email: p.email ?? "" })}
                  <Plus className="ml-2 h-4 w-4 shrink-0 text-brand" />
                </DropdownMenuItem>
              ))}
            </>
          )}
        </div>
      ) : null}

      {shareLine ? (
        <p className="px-2 pb-1.5 text-xs text-muted-foreground">{shareLine}</p>
      ) : null}
      <DropdownMenuSeparator />
      </>
      )}
      <DropdownMenuLabel className={label}>Who has access</DropdownMenuLabel>
      <div className="max-h-64 overflow-y-auto">
        {access.error ? (
          <p className="px-2 py-2 text-sm text-muted-foreground">{errorText(access.error)}</p>
        ) : !data ? (
          <p className="px-2 py-2 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {[
              ...(data.owner ? [{ p: data.owner, tag: "Owner", removable: false }] : []),
              // ADR-051: an invitee outside the company keeps the invite but
              // not the meeting while sharing is inside-only. The note sits
              // under the email (the phone's layout), so the name keeps its
              // room next to the ✕ (B45).
              ...data.invitees.map((p) => ({
                p,
                tag: "Invited",
                note: p.hasAccess === false ? "No access: outside your company" : undefined,
                removable: canRemovePerson(p, "invitee", canRemove),
              })),
            ].map(({ p, tag, note, removable }: { p: SharePerson; tag: string; note?: string; removable: boolean }) => (
              <div
                key={`${tag}:${p.userId || p.email}`}
                className={`${removable ? "group hover:bg-accent " : ""}flex items-center rounded-sm px-2 py-1.5 text-sm`}
              >
                <span className="mr-2 flex shrink-0">
                  <Avatar p={p} size="sm" />
                </span>
                {personLine(p, note)}
                <span className="ml-2 max-w-[9rem] shrink-0 text-right text-xs text-muted-foreground">
                  {tag}
                </span>
                {removable ? removeButton(p, "invitee") : null}
              </div>
            ))}
            {data.shared.map((p) => (
              <div
                key={`shared:${p.userId || p.email}`}
                className="group flex items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <span className="mr-2 flex shrink-0">
                  <Avatar p={p} size="sm" />
                </span>
                {personLine(p)}
                {canRemovePerson(p, "shared", canRemove) ? removeButton(p, "shared") : null}
              </div>
            ))}
            {data.shared.length === 0 ? (
              <p className="px-2 pb-1 pt-1 text-xs text-muted-foreground">
                Not shared with anyone else yet.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
